/* ========================================
   COLLECTION FILTER SYSTEM - STATE MANAGEMENT
   ========================================
   
   This file contains state management modules:
   - FilterState: Single source of truth for filter selections/applications
   - AccordionManager: Accordion open/closed state persistence
   
   Dependencies: CONFIG, Utils (from collection-filter.js)
   ======================================== */

(function() {
  'use strict';
  
  // Wait for CONFIG and Utils to be available
  if (typeof window.FilterUtils === 'undefined') {
    console.error('[FilterState] FilterUtils not found. Make sure collection-filter.js loads first.');
    return;
  }
  
  const { CONFIG, Utils } = window.FilterUtils;

  // ========================================
  // ACCORDION STATE MANAGEMENT
  // ========================================
  
  const AccordionManager = {
    saveAccordionStates() {
      const accordions = document.querySelectorAll(CONFIG.selectors.accordionDetails);
      const states = {};
      
      accordions.forEach(accordion => {
        const id = accordion.getAttribute('data-accordion-details');
        states[id] = accordion.hasAttribute('open');
      });
      
      Utils.setLocalStorage(CONFIG.localStorageKeys.accordionStates, JSON.stringify(states));
    },

    restoreAccordionStates() {
      const savedStates = Utils.getLocalStorage(CONFIG.localStorageKeys.accordionStates);
      
      if (!savedStates) return;
      
      try {
        const states = JSON.parse(savedStates);
        const accordions = document.querySelectorAll(CONFIG.selectors.accordionDetails);
        
        accordions.forEach(accordion => {
          const id = accordion.getAttribute('data-accordion-details');
          if (states.hasOwnProperty(id)) {
            if (states[id]) {
              accordion.setAttribute('open', '');
              accordion.setAttribute('aria-expanded', 'true');
            } else {
              accordion.removeAttribute('open');
              accordion.setAttribute('aria-expanded', 'false');
            }
          }
        });
        
      } catch (error) {
        Utils.error('Failed to restore accordion states:', error);
      }
    },

    bindAccordionEvents() {
      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-accordion-summary]')) {
          // Use requestAnimationFrame to allow accordion toggle before saving state
          requestAnimationFrame(() => {
            this.saveAccordionStates();
          });
        }
      });
    }
  };

  // ========================================
  // FILTER STATE MANAGEMENT
  // ========================================
  
  // Single source of truth for filter state - prevents sync issues
  const FilterState = {
    _state: {
      selected: new Set(),
      applied: new Set(),
      urls: new Map()
    },
    
    // Getters
    get selected() { return this._state.selected; },
    get applied() { return this._state.applied; },
    get urls() { return this._state.urls; },
    get selectedCount() { return this._state.selected.size; },
    get appliedCount() { return this._state.applied.size; },
    
    // Selection mutations
    addSelection(tagName, href = null) {
      this._state.selected.add(tagName);
      if (href) this._state.urls.set(tagName, href);
    },
    
    removeSelection(tagName) {
      this._state.selected.delete(tagName);
      this._state.urls.delete(tagName);
    },
    
    hasSelection(tagName) {
      return this._state.selected.has(tagName);
    },
    
    // Applied mutations
    syncAppliedFromSelected() {
      this._state.applied = new Set(this._state.selected);
    },
    
    addApplied(tagName, href = null) {
      this._state.applied.add(tagName);
      this._state.selected.add(tagName);
      if (href) this._state.urls.set(tagName, href);
    },
    
    // Clear operations
    clearSelections() {
      this._state.selected.clear();
      this._state.urls.clear();
    },
    
    clearApplied() {
      this._state.applied.clear();
    },
    
    clearAll() {
      this._state.selected.clear();
      this._state.applied.clear();
      this._state.urls.clear();
    },
    
    // Check if there are pending changes
    hasChanges() {
      if (this.selectedCount !== this.appliedCount) return true;
      const selectedArr = Array.from(this._state.selected).sort();
      const appliedArr = Array.from(this._state.applied).sort();
      return !selectedArr.every((tag, i) => tag === appliedArr[i]);
    },
    
    // Get URL for a tag
    getUrl(tagName) {
      return this._state.urls.get(tagName);
    }
  };

  // ========================================
  // EXPOSE TO GLOBAL SCOPE
  // ========================================
  
  window.FilterState = FilterState;
  window.AccordionManager = AccordionManager;

})();
